import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { tap, map, catchError } from 'rxjs/operators';
import { LoggerService } from './logger.service';
import { StorageService } from './storage.service';
import { AuthService } from './auth.service';
import { TranslateService } from '@ngx-translate/core';
import { SHARED_ENV } from '../tokens';

class FeError {
  public message: string;
  public type: string;
  public data: any;

  constructor(message: string, type: string, data?: any) {
    this.message = message;
    this.data = data;
    this.type = type;
  }

  public toString = (): string => {
    return this.message;
  };
}

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private readonly env = inject(SHARED_ENV);

  constructor(
    private storage: StorageService,
    private logger: LoggerService,
    private router: Router,
    private authService: AuthService,
    private translate: TranslateService
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (req.url.indexOf('/api/') === -1) {
      return next.handle(req);
    }

    let lang = localStorage.getItem('lang');
    if (lang == null) {
      lang = 'en';
    }
    req = req.clone({
      setHeaders: {
        // 'Content-Type': 'application/json',
        'Accept-Language': lang,
      },
    });

    if (!req.headers.has('Authorization')) {
      const token = this.storage.getItem('token');
      if (token) {
        req = req.clone({
          setHeaders: {
            Authorization: token,
          },
        });
      }
    }

    const started = Date.now();
    return next.handle(req).pipe(
      tap((event) => {
        if (event instanceof HttpResponse) {
          const elapsed = Date.now() - started;
          this.logger.debug(
            `Request for ${req.urlWithParams} took ${elapsed} ms.`
          );
        }
      }),
      map((event) => {
        if (event instanceof HttpResponse) {
          if (event.body == null || typeof event.body === 'string') {
            return event;
          }
          if (!event.body.status) {
            if (event.body.type === 'ERROR_UNAUTHORIZED') {
              this.unauthorizedHandler();
            }

            if (event.body.code === 503) {
              this.authService.clearStorage();
              this.router.navigate(['/sessions/maintenance']);
            }

            if (event.body.code === 801) {
              this.router.navigate(['/tfa']);
            }

            const messageText = this.constructErrorMessage(event.body);
            if (event.body.data == null) {
              this.logger.error(`Error: ${req.urlWithParams}`);
              if (event.body.code === 559) {
                // NEED_VERIFICATION_VIA_EMAIL
                throw new FeError(messageText, event.body.type);
              }

              throw new Error(messageText);
            } else {
              this.logger.error(`Error: ${req.urlWithParams}`);
              throw new FeError(messageText, event.body.type, event.body.data);
            }
          } else {
            return event.clone({ body: event.body.result });
          }
        }
        return event;
      }),
      catchError((err: any) => {
        if (err instanceof HttpErrorResponse) {
          return throwError(() => new Error('Network Error'));
        }
        return throwError(() => err);
      })
    );
  }

  private unauthorizedHandler(): void {
    this.authService.clearStorage();

    setTimeout(() => {
      window.location.reload();
    }, 100);
  }

  private constructErrorMessage(responseBody: any): string {
    const translatedMessage = this.translate.instant(
      `${this.env.beErrorLangKey}.${responseBody.type}`
    );
    if (translatedMessage !== responseBody.type && responseBody.code !== 552) {
      return translatedMessage;
    }
    return responseBody.message;
  }
}
